import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { CalendarBlank, CheckCircle, ArrowRight, Trophy, HeartStraight, Warning, Barbell, ForkKnife, ChatCircleText, Star, Smiley, SmileyMeh, SmileySad, Pulse } from "@phosphor-icons/react";

/**
 * SundayCheckin — 7-question weekly reflection form. Clients complete every
 * Sunday between 7am–8pm; coach reviews Sunday evening and adjusts each
 * client's workout + nutrition plan for the week ahead. Auto-saves draft to
 * localStorage so partial answers survive tab-close / navigation.
 *
 * Includes the Wellness Tracker at the START of the flow — 7 sliders + mood
 * — mirrored server-side into `wellness_checkins` so the fortnightly chart +
 * coach low-wellness pushes stay accurate.
 */
const DRAFT_KEY = "ll:sunday-checkin-draft";

const WELLNESS_SLIDERS = [
  { key: "sleep", label: "Sleep" },
  { key: "energy", label: "Energy" },
  { key: "libido", label: "Libido" },
  { key: "water_intake", label: "Water intake" },
  { key: "physical_activity", label: "Physical activity" },
  { key: "body_image", label: "Body image" },
  { key: "stress_levels", label: "Stress" },
];

const emptyWellness = {
  sleep: 5, energy: 5, libido: 5, water_intake: 5,
  physical_activity: 5, body_image: 5, stress_levels: 5,
  mood: "flat",
};

const emptyForm = {
  on_track_score: 3,
  health_wins: "",
  life_wins: "",
  distractions: "",
  exercise_likes: "",
  exercise_dislikes: "",
  meal_likes: "",
  meal_dislikes: "",
  anything_else: "",
  wellness: emptyWellness,
};

export default function SundayCheckin() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  // Load existing check-in for this week (if any) + hydrate any local draft
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/client/sunday-checkin/current");
        if (data.checkin) {
          setForm({
            on_track_score: data.checkin.on_track_score ?? 3,
            health_wins: data.checkin.health_wins || "",
            life_wins: data.checkin.life_wins || "",
            distractions: data.checkin.distractions || "",
            exercise_likes: data.checkin.exercise_likes || "",
            exercise_dislikes: data.checkin.exercise_dislikes || "",
            meal_likes: data.checkin.meal_likes || "",
            meal_dislikes: data.checkin.meal_dislikes || "",
            anything_else: data.checkin.anything_else || "",
            wellness: { ...emptyWellness, ...(data.checkin.wellness || {}) },
          });
          setExisting(data.checkin);
        } else {
          try {
            const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
            if (draft && draft.iso_week === data.iso_week) {
              setForm({
                ...emptyForm,
                ...draft.form,
                wellness: { ...emptyWellness, ...(draft.form?.wellness || {}) },
              });
            }
          } catch { /* ignore corrupt draft */ }
        }
      } catch (e) { setErr(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Auto-save draft on every change so a Safari tab close doesn't lose work
  useEffect(() => {
    if (loading || existing) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        iso_week: new Date().toISOString().slice(0, 10),
        form,
      }));
    } catch { /* localStorage blocked */ }
  }, [form, loading, existing]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateWellness = (k, v) => setForm((f) => ({ ...f, wellness: { ...f.wellness, [k]: v } }));

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      await http.post("/client/sunday-checkin", form);
      setSubmitted(true);
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      setTimeout(() => navigate("/app"), 2200);
    } catch (e) { setErr(apiError(e, "Couldn't submit.")); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-xs text-zinc-500 font-manrope">Loading…</div>;

  if (submitted) {
    return (
      <div className="p-6 md:p-10 max-w-2xl mx-auto text-center py-16" data-testid="sunday-checkin-thanks">
        <CheckCircle size={56} weight="fill" className="text-[#00CC66] mx-auto mb-4" />
        <div className="font-oswald text-3xl uppercase text-white mb-2">Locked in for the week</div>
        <p className="text-zinc-400 font-manrope text-sm max-w-md mx-auto">Coach will review Sunday evening and tune your plan. Heading you back to Today…</p>
      </div>
    );
  }

  const alreadySubmitted = !!existing;
  const scoreLabels = { 1: "Way off", 2: "Slipping", 3: "Mixed bag", 4: "On it", 5: "Locked in" };
  const scoreColor = form.on_track_score <= 2 ? "text-[#FF3333]" : form.on_track_score >= 4 ? "text-[#00CC66]" : "text-yellow-400";

  return (
    <div className="p-6 md:p-10 max-w-2xl mx-auto pb-24" data-testid="sunday-checkin-page">
      <div className="id-label mb-3 flex items-center gap-2"><CalendarBlank size={12} weight="fill" /> — Sunday check-in —</div>
      <h1 className="id-h1 mb-2">Reset the week</h1>
      <p className="text-zinc-400 font-manrope text-sm mb-8">
        2 minutes of honest reflection so Coach can tune your workouts + nutrition for the week ahead. All answers go straight to Jimmy — nobody else.
      </p>

      {alreadySubmitted && (
        <div className="mb-6 border border-[#00CC66]/40 bg-[#00CC66]/5 px-4 py-3 flex items-start gap-3" data-testid="sunday-already-submitted">
          <CheckCircle size={18} weight="fill" className="text-[#00CC66] shrink-0 mt-0.5" />
          <div className="text-xs font-manrope text-zinc-300">
            <span className="text-[#00CC66] font-bold">Already submitted this week.</span> Editing below will update your answers — coach hasn&apos;t reviewed yet.
          </div>
        </div>
      )}

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-4" data-testid="sunday-error">{err}</div>}

      <div className="space-y-6">
        {/* Wellness tracker — front of the form so Coach gets the numbers
            even if the client bails on the text answers below. */}
        <div className="id-card" data-testid="sunday-wellness-block">
          <div className="id-label mb-1 flex items-center gap-1.5"><Pulse size={11} weight="fill" /> Wellness this week</div>
          <p className="text-[11px] text-zinc-500 font-manrope mb-4">0 = worst · 10 = best</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {WELLNESS_SLIDERS.map((r) => (
              <div key={r.key} data-testid={`sunday-well-${r.key}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-oswald uppercase tracking-wide text-sm">{r.label}</span>
                  <span className="font-oswald text-xl text-[#FF3333] font-bold">{form.wellness[r.key]}</span>
                </div>
                <input
                  type="range" min={0} max={10} step={1}
                  value={form.wellness[r.key]}
                  onChange={(e) => updateWellness(r.key, Number(e.target.value))}
                  className="w-full accent-[#FF3333]"
                  data-testid={`sunday-well-${r.key}-slider`}
                />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="id-label mb-2">Mood</div>
            <div className="grid grid-cols-3 gap-2" data-testid="sunday-well-mood">
              {[
                { v: "sad", Icon: SmileySad, l: "Sad" },
                { v: "flat", Icon: SmileyMeh, l: "Flat" },
                { v: "happy", Icon: Smiley, l: "Happy" },
              ].map(({ v, Icon, l }) => (
                <button
                  key={v} type="button"
                  onClick={() => updateWellness("mood", v)}
                  className={`py-3 border flex flex-col items-center gap-1 transition-colors ${form.wellness.mood === v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}
                  data-testid={`sunday-well-mood-${v}`}
                >
                  <Icon size={24} weight="bold" />
                  <span className="text-xs uppercase tracking-[0.15em] font-bold">{l}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* On-track score */}
        <div className="id-card">
          <div className="id-label mb-3 flex items-center gap-1.5"><Star size={11} weight="fill" /> How on track do you feel this week?</div>
          <div className="grid grid-cols-5 gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => update("on_track_score", n)}
                className={
                  "py-4 border-2 font-oswald text-2xl transition-all " +
                  (form.on_track_score === n
                    ? "border-[#FF3333] bg-[#FF3333]/10 text-white"
                    : "border-[#262626] text-zinc-500 hover:border-white hover:text-white")
                }
                data-testid={`sunday-score-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className={"text-xs font-manrope mt-2 text-center " + scoreColor} data-testid="sunday-score-label">
            {scoreLabels[form.on_track_score]}
          </div>
        </div>

        <Question
          icon={Trophy}
          label="Health + fitness wins this week"
          placeholder="Hit a PR? Nailed 5 workouts? Cooked all your meals? Even small wins — write them down."
          value={form.health_wins}
          onChange={(v) => update("health_wins", v)}
          testid="sunday-health-wins"
        />
        <Question
          icon={HeartStraight}
          label="Life wins outside the gym"
          placeholder="Kids, work, relationships, hobbies. Promotion? Great date night? Finished a project? Coach wants to know."
          value={form.life_wins}
          onChange={(v) => update("life_wins", v)}
          testid="sunday-life-wins"
        />
        <Question
          icon={Warning}
          label="What's distracting you or in the way?"
          placeholder="Late nights? Work stress? Kids sick? Travel? Anything pulling focus this week."
          value={form.distractions}
          onChange={(v) => update("distractions", v)}
          testid="sunday-distractions"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Question
            icon={Barbell}
            label="Exercises you loved"
            placeholder="e.g. Bulgarian split squats, farmer walks…"
            value={form.exercise_likes}
            onChange={(v) => update("exercise_likes", v)}
            testid="sunday-exercise-likes"
            compact
          />
          <Question
            icon={Barbell}
            label="Exercises you disliked"
            placeholder="e.g. Overhead press hurt shoulder…"
            value={form.exercise_dislikes}
            onChange={(v) => update("exercise_dislikes", v)}
            testid="sunday-exercise-dislikes"
            compact
          />
          <Question
            icon={ForkKnife}
            label="Meals you loved"
            placeholder="e.g. Chicken burrito bowl, overnight oats…"
            value={form.meal_likes}
            onChange={(v) => update("meal_likes", v)}
            testid="sunday-meal-likes"
            compact
          />
          <Question
            icon={ForkKnife}
            label="Meals you disliked"
            placeholder="e.g. Salmon — too fishy, protein shake…"
            value={form.meal_dislikes}
            onChange={(v) => update("meal_dislikes", v)}
            testid="sunday-meal-dislikes"
            compact
          />
        </div>
        <Question
          icon={ChatCircleText}
          label="Anything else Coach should know?"
          placeholder="Injuries, upcoming travel, family stuff, questions, requests… anything."
          value={form.anything_else}
          onChange={(v) => update("anything_else", v)}
          testid="sunday-anything-else"
        />
      </div>

      <div className="sticky bottom-0 left-0 right-0 mt-8 -mx-6 md:-mx-10 px-6 md:px-10 py-4 bg-black/95 backdrop-blur border-t border-[#262626]">
        <button
          onClick={submit}
          disabled={saving}
          className="w-full id-btn-primary text-sm py-3 disabled:opacity-40"
          data-testid="sunday-submit-btn"
        >
          {saving ? "Sending to Coach…" : alreadySubmitted ? "Update my check-in" : "Send to Coach"}
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function Question({ icon: Icon, label, placeholder, value, onChange, testid, compact }) {
  return (
    <div className="id-card">
      <div className="id-label mb-2 flex items-center gap-1.5">
        <Icon size={11} weight="fill" /> {label}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={compact ? 2 : 3}
        maxLength={1500}
        className="w-full bg-[#0F0F0F] border border-[#262626] text-white text-sm font-manrope px-3 py-2 focus:border-[#FF3333] outline-none resize-none"
        data-testid={testid}
      />
    </div>
  );
}
